/**
 * Combine, finalize, extract — BIP-174 roles 4, 5 and 6.
 *
 * These are MECHANICAL and trustless: merging partial signatures and assembling witnesses
 * cannot change what gets paid. They run client-side and appear in the UI as system steps
 * precisely so nobody mistakes assembly for another approval.
 */
import * as btc from '@scure/btc-signer';
import { secp256k1 } from '@noble/curves/secp256k1.js';
import { bytesToHex } from '@noble/hashes/utils.js';
import { findMine } from './descriptors.js';
export class PsbtError extends Error {
    code;
    constructor(code, message) {
        super(`${code}: ${message}`);
        this.code = code;
        this.name = 'PsbtError';
    }
}
const open = (psbt) => {
    try {
        return btc.Transaction.fromPSBT(psbt, { allowUnknownOutputs: true, allowUnknownInputs: true });
    }
    catch (e) {
        throw new PsbtError('MALFORMED_PSBT', e.message);
    }
};
/** True once every input carries a final scriptSig/witness — nothing left to sign. */
export function isFinalized(psbt) {
    return open(psbt).isFinal;
}
/**
 * Distinct master fingerprints named by the PSBT's OWN metadata.
 *
 * 🚨 **THE ANSWER IS ATTACKER-CONTROLLED. NEVER COUNT IT TOWARDS A THRESHOLD.**
 *
 * `bip32Derivation[].fingerprint` is a field whoever built the PSBT wrote. A coordinator holding
 * no vault key at all can generate two throwaway keys, sign with them, and label the derivations
 * with two of the vault's real fingerprints — which are public, they are printed in the
 * descriptor. This function will then name those two real signers.
 *
 * That is not hypothetical: until 2026-08-27 `resolveSigners` UNIONED this result into the set
 * the risk catalog counts, so an audit produced `PASS POLICY_MET: 2 of the 2 required signatures
 * collected` on a PSBT no vault key had touched, naming two real people. The union could only
 * ever ADD names, so the trustworthy route could not correct it.
 *
 * It survives for two honest uses — displaying what a PSBT CLAIMS, and working without a
 * descriptor — and for neither of them may the result reach `policyMet`. Use
 * `signerFingerprintsFor`, which resolves identity from the descriptor and verifies the
 * signature bytes.
 *
 * ⚠️ Also note it comes back EMPTY on a Core-produced PSBT: Bitcoin Core strips
 * `bip32Derivation` from an input once it has signed it.
 */
export function signerFingerprints(psbt) {
    const tx = open(psbt);
    const seen = new Set();
    for (let i = 0; i < tx.inputsLength; i++) {
        const inp = tx.getInput(i);
        const sigs = inp.partialSig ?? [];
        const derivations = inp.bip32Derivation ?? [];
        for (const [pubkey] of sigs) {
            const hit = derivations.find(([p]) => p.length === pubkey.length &&
                p.every((b, k) => b === pubkey[k]));
            if (hit) {
                const fpNum = hit[1].fingerprint >>> 0;
                seen.add(fpNum.toString(16).padStart(8, '0'));
            }
        }
    }
    return [...seen].sort();
}
/**
 * Merge partial signatures from independent signers. Order-independent.
 *
 * Parses through this module's own `open()` rather than through `btc.PSBTCombine`, which
 * re-parses with the library DEFAULTS. Every other entry point here — `deriveIntent`, the risk
 * catalog, `finalize` — opens PSBTs with `allowUnknownOutputs`, so a transaction carrying an
 * OP_RETURN could be loaded, inspected, scored and signed, and then failed at the one step that
 * merges the signatures. The signatures were valid and there was no way to use them.
 *
 * `tx.combine` still refuses two PSBTs describing different transactions, which is the check
 * that matters: merging is only trustless because it cannot change what gets paid.
 */
export function combine(psbts) {
    if (psbts.length === 0)
        throw new PsbtError('NOTHING_TO_COMBINE', 'no PSBTs given');
    // A single PSBT used to be returned untouched, so `combine([garbage])` handed the caller its
    // garbage back with the authority of having been "combined". One input still gets parsed.
    const tx = open(psbts[0]);
    for (const other of psbts.slice(1)) {
        try {
            tx.combine(open(other));
        }
        catch (e) {
            throw new PsbtError('COMBINE_FAILED', e.message);
        }
    }
    return tx.toPSBT(0);
}
/** Assemble witnesses. Throws if the collected signatures do not satisfy the script. */
export function finalize(psbt) {
    const tx = open(psbt);
    if (tx.isFinal)
        throw new PsbtError('ALREADY_FINALIZED', 'every input is already final');
    try {
        tx.finalize();
    }
    catch (e) {
        throw new PsbtError('FINALIZE_FAILED', `signatures do not satisfy the script: ${e.message}`);
    }
    return tx.toPSBT(0);
}
/** Produce the broadcastable transaction. Only valid once finalized. */
export function extract(psbt) {
    const tx = open(psbt);
    if (!tx.isFinal)
        throw new PsbtError('NOT_FINALIZED', 'finalize before extracting');
    return { raw: tx.extract(), txid: tx.id, vsize: tx.vsize };
}
const sameBytes = (a, b) => a.length === b.length && a.every((x, i) => x === b[i]);
/** Does `pubkey` have any role in this input — as a derivation entry or inside its script? */
function pubkeyBelongsToInput(input, pubkey) {
    if ((input.bip32Derivation ?? []).some(([p]) => sameBytes(p, pubkey)))
        return true;
    const needle = bytesToHex(pubkey);
    for (const script of [input.witnessScript, input.redeemScript, input.witnessUtxo?.script]) {
        if (script && bytesToHex(script).includes(needle))
            return true;
    }
    return false;
}
export function addPartialSignatures(psbt, sigs) {
    if (sigs.length === 0)
        throw new PsbtError('NO_SIGNATURES', 'nothing to add');
    const tx = open(psbt);
    for (const { inputIndex, pubkey, signature } of sigs) {
        // `Number.isInteger` matters as much as the bounds: 1.5 passes `>= 0 && < inputsLength`,
        // and `getInput(1.5)` returns undefined, so the code below read `.partialSig` off nothing
        // and the write landed nowhere. A signature silently dropped on the floor looks exactly
        // like a signer who never answered.
        if (!Number.isInteger(inputIndex) || inputIndex < 0 || inputIndex >= tx.inputsLength)
            throw new PsbtError('BAD_INPUT_INDEX', `signature refers to input ${inputIndex}, but this transaction has ${tx.inputsLength} ` +
                `input(s) and an index must be a whole number`);
        const input = tx.getInput(inputIndex);
        // The key must actually appear in this input, either in its derivation metadata or in the
        // witnessScript it is spending. Accepting any pubkey let a caller write a signature from a
        // key that has no role in the input at all — it can never satisfy the script, so the PSBT
        // grows a signature that looks like progress towards the threshold and is not.
        if (!pubkeyBelongsToInput(input, pubkey))
            throw new PsbtError('UNKNOWN_PUBKEY', `the key ${bytesToHex(pubkey).slice(0, 16)}… appears neither in input ${inputIndex}'s ` +
                `derivation metadata nor in the script it spends, so its signature can never satisfy it`);
        const existing = input.partialSig ?? [];
        // A signer repeating itself is harmless; a signer contradicting itself is not.
        const clash = existing.find(([p, s]) => sameBytes(p, pubkey) && !sameBytes(s, signature));
        if (clash)
            throw new PsbtError('CONFLICTING_SIGNATURE', `input ${inputIndex} already carries a DIFFERENT signature for this key`);
        const merged = existing.some(([p]) => sameBytes(p, pubkey))
            ? existing
            : [...existing, [pubkey, signature]];
        tx.updateInput(inputIndex, { partialSig: merged }, true);
    }
    return tx.toPSBT(0);
}
/**
 * Which of the vault's keys have signed — resolved from the DESCRIPTOR, not from PSBT metadata.
 *
 * `signerFingerprints` asks the PSBT who signed, by matching each `partialSig` pubkey against the
 * input's `bip32Derivation` entries. Bitcoin Core strips those entries once it signs, so that
 * question comes back empty on a PSBT Core produced — and the room then cannot tell whether the
 * threshold has been met, or whether a key has voted twice. Found by feeding a real Core-signed
 * PSBT into the app, which reported "carries no signatures".
 *
 * This asks the vault instead. For each signed input we already know its derivation path (the
 * same `isMine` scan the intent panel uses, and the results are cached), so the descriptor tells
 * us exactly which pubkey belongs to which of the n keys — and therefore which fingerprint —
 * without the PSBT having to remember anything.
 *
 * ⚠️ A fingerprint is returned only if it has signed **every** input this vault owns. Counting a
 * key that signed one input out of three let a room read "2 of 2 collected, threshold met" for a
 * transaction whose remaining inputs carried a single signature and could never be finalized —
 * and, worse, a partial signer was recorded as having approved the whole spend. The threshold is
 * a property of the transaction, so the attribution has to be too.
 *
 * ⚠️ **THE SIGNATURE BYTES ARE VERIFIED.** Until 2026-08-27 this asked only "is this pubkey one
 * of the n keys this vault derives?" and counted the entry — so 72 bytes of `0xaa` filed under a
 * real vault pubkey produced `PASS POLICY_MET`. Nothing in `packages/core` called a signature
 * primitive at all; `grep verify src/` had no hits. A threshold counted from unverified bytes is
 * not a threshold, and the failure is silent in the worst way: the room says "ready to
 * broadcast" and `finalize()` then refuses, after every human has already approved.
 */
export function signerFingerprintsFor(psbt, descriptor, window) {
    const perInput = vaultSignersPerInput(psbt, descriptor, window);
    if (perInput.length === 0)
        return [];
    const [first, ...rest] = perInput;
    const every = [...first].filter((fp) => rest.every((s) => s.has(fp)));
    return every.sort();
}
/** The vault fingerprints that signed each input the vault owns, in input order. */
function vaultSignersPerInput(psbt, descriptor, window) {
    const tx = open(psbt);
    const out = [];
    for (let i = 0; i < tx.inputsLength; i++) {
        const input = tx.getInput(i);
        // Which address does this input spend? Without that we cannot say which derived pubkey set
        // to compare against.
        const script = inputScript(input);
        if (!script)
            continue;
        const derived = findMine(descriptor, script, window);
        if (!derived)
            continue; // not ours; not our threshold to count
        // `pubkeys` is in DESCRIPTOR order, so position k is descriptor.keys[k].
        const byHex = new Map(derived.pubkeys.map((p, k) => [bytesToHex(p), k]));
        const signed = new Set();
        for (const [pubkey, sig] of input.partialSig ?? []) {
            const k = byHex.get(bytesToHex(pubkey));
            if (k === undefined)
                continue; // a signature from a key outside this vault
            // Belonging to the vault is not the same as having signed. Check the bytes.
            if (!verifiesWitnessV0(tx, i, derived, input, pubkey, sig))
                continue;
            const fp = descriptor.keys[k]?.origin?.fingerprint;
            if (fp)
                signed.add(fp);
        }
        out.push(signed);
    }
    return out;
}
/**
 * Does this signature actually commit this key to this input of this transaction?
 *
 * P2WSH is BIP-143: the sighash covers the WITNESS SCRIPT (the m-of-n script the coins are
 * locked to), not the scriptPubKey, plus the input's amount. Both come from places an attacker
 * cannot quietly move — the witness script is re-derived from the vault descriptor by
 * `findMine`, and the amount comes from the input's own UTXO data, which `deriveIntent` and the
 * risk catalog have already scrutinised.
 *
 * The sighash TYPE is read from the signature's own trailing byte, because that is what the
 * signer committed to. Reading it from `input.sighashType` instead would let a PSBT declare one
 * type and carry a signature over another; the risk catalog reports a non-`ALL` sighash
 * separately, and that reporting is not this function's job.
 *
 * Returns false rather than throwing on anything malformed: one unparseable signature in a
 * multi-input PSBT is an ordinary "this one does not count", not a reason to abandon the count
 * and lose the signatures that were fine.
 */
function verifiesWitnessV0(tx, idx, derived, input, pubkey, sig) {
    const amount = input.witnessUtxo?.amount;
    if (typeof amount !== 'bigint')
        return false; // no amount, no BIP-143 preimage
    if (sig.length < 9)
        return false; // DER + 1 sighash byte, at minimum
    const hashType = sig[sig.length - 1];
    const der = sig.subarray(0, sig.length - 1);
    try {
        // `scriptCode`, not `witnessScript`. They are the same thing for a shared wsh() wallet and
        // are NOT for a single-key one, where there is no witness script at all. Using the wrong one
        // fails silently: every signature stops verifying and the threshold quietly stops being met.
        const preimage = tx.preimageWitnessV0(idx, derived.scriptCode, hashType, amount);
        // `prehash: false` — preimageWitnessV0 already returns the double-SHA256 sighash.
        // `lowS: true` — a DECISION, not the library default it happens to match today. Bitcoin
        // Core has enforced low-S as a standardness rule since 2015 (BIP-62/146), so a high-S
        // signature is one no modern signer emits and no node relays; accepting it would count a
        // malleated signature towards the threshold. Written out so a noble upgrade cannot flip it.
        return secp256k1.verify(der, preimage, pubkey, { prehash: false, format: 'der', lowS: true });
    }
    catch {
        return false;
    }
}
/** The scriptPubKey this input spends, from witness_utxo or from the full previous transaction. */
function inputScript(input) {
    if (input.witnessUtxo?.script)
        return input.witnessUtxo.script;
    const prev = input.nonWitnessUtxo;
    return prev?.outputs?.[input.index ?? 0]?.script ?? null;
}
/**
 * Prefix marking a signature this vault cannot attribute to any of its own keys.
 *
 * Never a threshold vote. A caller counting signatures towards `m` must filter these out; they
 * exist so that "somebody who is not one of your signers has written into this PSBT" is
 * reportable at all, rather than being silently discarded.
 */
export const FOREIGN_SIGNER_PREFIX = 'unknown:';
/** Signatures present in the PSBT that belong to no key in this vault. */
export function foreignSignatures(psbt, descriptor, window) {
    const tx = open(psbt);
    const foreign = new Set();
    for (let i = 0; i < tx.inputsLength; i++) {
        const input = tx.getInput(i);
        const sigs = input.partialSig ?? [];
        if (sigs.length === 0)
            continue;
        const script = inputScript(input);
        const derived = script ? findMine(descriptor, script, window) : null;
        const known = new Set((derived?.pubkeys ?? []).map((p) => bytesToHex(p)));
        for (const [pubkey] of sigs) {
            const hex = bytesToHex(pubkey);
            if (!known.has(hex))
                foreign.add(`${FOREIGN_SIGNER_PREFIX}${hex.slice(0, 16)}`);
        }
    }
    return [...foreign].sort();
}
/**
 * Everything known about who wrote into this PSBT: vault keys plus anything unattributable.
 *
 * The two identification routes fail in opposite situations — the descriptor route cannot name a
 * key outside the vault, and the metadata route disappears when a wallet strips `bip32Derivation`
 * — so both run and the results are merged. Preferring one and stopping made a signature from an
 * unregistered key **invisible**: the descriptor route found the honest signer, returned early,
 * and nothing anywhere reported that a stranger had also signed. A signature that cannot be
 * attributed is not the same as a signature that is not there, and only one of those is worth
 * showing a human.
 *
 * Entries beginning with `unknown:` are NOT vault keys — see `FOREIGN_SIGNER_PREFIX`.
 */
export function resolveSigners(psbt, descriptor, window) {
    /*
     * 🚨 WITH A DESCRIPTOR, THE METADATA ROUTE IS NOT CONSULTED AT ALL.
     *
     * This used to be `new Set(signerFingerprints(psbt))` unioned with the descriptor route, on
     * the reasoning that the two fail in opposite situations so both should run. The reasoning is
     * right about FOREIGN signatures and wrong about vault ones, and the difference is that
     * `signerFingerprints` reads a fingerprint the PSBT's author wrote. A union can only ADD, so
     * the trustworthy route had no way to contradict a forged claim, and a coordinator with no
     * vault key produced `PASS POLICY_MET` naming two real signers (audit, 2026-08-27).
     *
     * The metadata route also adds nothing here. `signerFingerprintsFor` matches pubkeys against
     * keys re-derived from the descriptor and then verifies the signature — it never needed
     * `bip32Derivation`, which is exactly why it works on Core-produced PSBTs where the metadata
     * route comes back empty. So with a descriptor in hand there is no case where the untrusted
     * answer is the only answer.
     *
     * `foreignSignatures` stays: it reports pubkeys that are NOT vault keys, is derived from the
     * bytes rather than from a label, and every entry it returns carries the `unknown:` prefix
     * that keeps it out of the threshold.
     */
    if (descriptor && window) {
        const all = new Set(signerFingerprintsFor(psbt, descriptor, window));
        for (const fp of foreignSignatures(psbt, descriptor, window))
            all.add(fp);
        return [...all].sort();
    }
    // No descriptor: the metadata route is all there is. Callers in this position cannot judge a
    // threshold anyway — `risk.ts` treats an absent descriptor as POLICY_UNCHECKED.
    return [...new Set(signerFingerprints(psbt))].sort();
}
/** The subset of `resolveSigners` that may count towards the vault's threshold. */
export const vaultSigners = (signers) => signers.filter((s) => !s.startsWith(FOREIGN_SIGNER_PREFIX));
//# sourceMappingURL=psbt.js.map